import React, { useEffect, useMemo, useState } from 'react';
import { Grid, InputAdornment, MenuItem, Paper, TextField } from '@mui/material';
import { Box } from '@mui/system';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { debounce } from 'lodash';
import SearchIcon from '@mui/icons-material/Search';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { toast } from 'react-toastify';
import { ConfirmationDialog, DataGrid, IconBtnBase } from 'Components';
import { CERTIFICATEPRINTLIST } from 'Constants/QueriesKeys';
import {
    CERTIFICATE_PER_PAGE,
    CERTIFICATE_PER_PAGE_OPTIONS,
    SEARCH_DEBOUNCE_MS,
} from 'Constants/CertificateNames';
import { deleteCertificatePrint, fetchCertificatePrints } from 'Services/CertificateNamesSvc';
import { formatDateTime, formatSimilarity, formatText } from './CertificateHelpers';

const styles = {
    tableSize: {
        height: 650,
        width: '100%',
    },
};

/**
 * The print log: the certificates that were actually opened for printing, under
 * which approved name, and when.
 *
 * This endpoint takes `direction` but no `sort_by`, so the ordering is offered
 * as a newest/oldest choice rather than as sortable column headers that would
 * only ever reorder the 25 rows already on screen.
 */
const PrintsTable = ({ registerRefetch }) => {
    const { t } = useTranslation();
    const spacing = 4;

    const [printList, setPrintList] = useState([]);
    const [paginator1, setPaginator1] = useState(0);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(CERTIFICATE_PER_PAGE);
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [direction, setDirection] = useState('desc');
    const [recordToDelete, setRecordToDelete] = useState(null);

    const applySearch = useMemo(
        () =>
            debounce((value) => {
                setSearch(value);
                setPage(1);
            }, SEARCH_DEBOUNCE_MS),
        [],
    );

    useEffect(() => () => applySearch.cancel(), [applySearch]);

    const printListRender = useQuery(
        [CERTIFICATEPRINTLIST, { page, perPage, search, direction }],
        () =>
            fetchCertificatePrints({
                page,
                per_page: perPage,
                search,
                direction,
            }).then((list) => {
                setPrintList(list.rows);
                setPaginator1(list.total);

                return list;
            }),
        { keepPreviousData: true },
    );

    const { refetch } = printListRender;

    useEffect(() => {
        registerRefetch(refetch);
    }, [registerRefetch, refetch]);

    const handleSearchChange = (event) => {
        setSearchInput(event.target.value);
        applySearch(event.target.value);
    };

    /**
     * Delete one print record. Only this row goes — the attempt behind it is a
     * parent, not a dependant. Stepping back a page when this was the last row
     * on it refetches through the query key.
     */
    const handleDelete = async () => {
        const record = recordToDelete;
        setRecordToDelete(null);

        try {
            await deleteCertificatePrint(record.id);
            toast.success(t('toast.success.deletedSuccessfully'));

            if (printList.length === 1 && page > 1) {
                setPage(page - 1);
            } else {
                refetch();
            }
        } catch (error) {
            toast.error(error?.response?.data?.message);
        }
    };

    const columns1 = [
        {
            field: 'action',
            headerName: t('common.actions'),
            width: 100,
            editable: false,
            sortable: false,
            renderCell: (row) => (
                <div className="flex items-center">
                    <IconBtnBase
                        title={t('common.delete')}
                        onClick={() => setRecordToDelete(row?.row)}
                        size="large"
                    >
                        <DeleteOutlineOutlinedIcon />
                    </IconBtnBase>
                </div>
            ),
        },
        {
            field: 'name',
            headerName: t('admin.certificateNames.history.printedName'),
            minWidth: 250,
            flex: 1,
            editable: false,
            sortable: false,
        },
        {
            field: 'created_at',
            headerName: t('admin.certificateNames.history.dateTime'),
            width: 180,
            editable: false,
            sortable: false,
            renderCell: (row) => formatDateTime(row.row.created_at),
        },
        {
            field: 'entered_name',
            headerName: t('admin.certificateNames.history.enteredName'),
            minWidth: 250,
            flex: 1,
            editable: false,
            sortable: false,
            renderCell: (row) => formatText(row.row.entered_name),
        },
        {
            field: 'similarity',
            headerName: t('admin.certificateNames.history.similarity'),
            width: 120,
            editable: false,
            sortable: false,
            renderCell: (row) => formatSimilarity(row.row.similarity),
        },
        {
            field: 'locale',
            headerName: t('admin.certificateNames.history.locale'),
            width: 100,
            editable: false,
            sortable: false,
            renderCell: (row) => formatText(row.row.locale),
        },
        {
            field: 'ip_address',
            headerName: t('admin.certificateNames.history.ipAddress'),
            width: 140,
            editable: false,
            sortable: false,
            renderCell: (row) => formatText(row.row.ip_address),
        },];

    return (
        <>
            <div className="mt-8">
                <p className="text-xs text-gray-500 pb-4">
                    {t('admin.certificateNames.history.printsHint')}
                </p>

                <Grid container spacing={spacing} alignItems="flex-end">
                    <Grid item xs={12} md={8}>
                        <h6 className="labelForm pb-2">
                            {t('admin.certificateNames.history.printsSearch')}
                        </h6>
                        <TextField
                            fullWidth
                            id="certificate-prints-search"
                            placeholder={t('admin.certificateNames.history.printsSearchPlaceholder')}
                            onChange={handleSearchChange}
                            value={searchInput}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <h6 className="labelForm pb-2">{t('admin.certificateNames.history.order')}</h6>
                        <TextField
                            select
                            fullWidth
                            id="certificate-prints-direction"
                            value={direction}
                            onChange={(event) => {
                                setDirection(event.target.value);
                                setPage(1);
                            }}
                        >
                            <MenuItem value="desc">
                                {t('admin.certificateNames.history.newestFirst')}
                            </MenuItem>
                            <MenuItem value="asc">
                                {t('admin.certificateNames.history.oldestFirst')}
                            </MenuItem>
                        </TextField>
                    </Grid>
                </Grid>

                <Box className="mt-8" sx={styles.tableSize}>
                    <Grid container spacing={spacing}>
                        <Grid item xs={12}>
                            <Paper>
                                <DataGrid
                                    rowCount={paginator1}
                                    pagination
                                    paginationMode="server"
                                    page={page - 1}
                                    onPageChange={(newPage) => setPage(newPage + 1)}
                                    pageSize={perPage}
                                    rowsPerPageOptions={CERTIFICATE_PER_PAGE_OPTIONS}
                                    onPageSizeChange={(newPageSize) => {
                                        setPerPage(newPageSize);
                                        setPage(1);
                                    }}
                                    loading={printListRender?.isLoading}
                                    rows={printList}
                                    columns={columns1}
                                />
                            </Paper>
                        </Grid>
                    </Grid>
                </Box>
            </div>

            <ConfirmationDialog
                isOpen={Boolean(recordToDelete)}
                title={t('admin.certificateNames.history.deletePrintTitle')}
                text={t('admin.certificateNames.history.deletePrintText')}
                onReject={() => setRecordToDelete(null)}
                onResolve={handleDelete}
            />
        </>
    );
};

export default PrintsTable;
